import { ApiError } from '@/lib/api/errors';
import type { TransactionCreate, TransactionUpdate } from '@/lib/api/types';

/** A write the user made that has not reached the server yet. */
export type OutboxOp =
  | { kind: 'create'; body: TransactionCreate }
  | { kind: 'update'; txId: string; body: TransactionUpdate }
  | { kind: 'delete'; txId: string };

export type OutboxEntry = OutboxOp & {
  /** Creates use their client_id (the server dedupes on it); other ops get a fresh uuid. */
  id: string;
  userId: string;
  workspaceId: string;
  createdAt: number;
  status: 'pending' | 'failed';
  attempts: number;
};

export interface Scope {
  userId: string;
  workspaceId: string;
}

export interface OutboxStore {
  all(): Promise<OutboxEntry[]>;
  put(entry: OutboxEntry): Promise<void>;
  remove(id: string): Promise<void>;
  clear(): Promise<void>;
}

export class MemoryStore implements OutboxStore {
  private rows = new Map<string, OutboxEntry>();
  all = async () => [...this.rows.values()];
  put = async (e: OutboxEntry) => void this.rows.set(e.id, e);
  remove = async (id: string) => void this.rows.delete(id);
  clear = async () => this.rows.clear();
}

export type Verdict = 'done' | 'retry' | 'failed';

/**
 * What a failed send means. Connection trouble, throttling, server errors and an expired session are temporary, so the
 * entry stays queued. A 404 on an edit or delete means the row is already gone: delete beats edit, nothing left to do.
 * Any other 4xx is a rejection the user has to look at.
 */
export function judge(op: OutboxOp, err: unknown): Verdict {
  if (!(err instanceof ApiError)) return 'retry'; // fetch rejects with a TypeError when offline
  if (err.status === 404 && op.kind !== 'create') return 'done';
  if (err.isNetwork || err.status === 401 || err.status === 408 || err.status === 429 || err.status >= 500) return 'retry';
  return 'failed';
}

export interface FlushResult {
  sent: number;
  failed: number;
  /** A temporary problem ended the run early; later entries keep their order for the next run. */
  stopped: boolean;
}

export type Send = (op: OutboxOp) => Promise<void>;

type Listener = () => void;

let lastStamp = 0;
/** Strictly increasing, so two writes in the same millisecond still keep the order the user made them in. */
function nextStamp(): number {
  lastStamp = Math.max(Date.now(), lastStamp + 1);
  return lastStamp;
}

export class Outbox {
  private listeners = new Set<Listener>();
  constructor(private store: OutboxStore) {}

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private changed() {
    this.listeners.forEach((fn) => fn());
  }

  async list(scope: Scope): Promise<OutboxEntry[]> {
    const rows = await this.store.all();
    return rows
      .filter((e) => e.userId === scope.userId && e.workspaceId === scope.workspaceId)
      .sort((a, b) => a.createdAt - b.createdAt);
  }

  async enqueue(scope: Scope, op: OutboxOp): Promise<OutboxEntry> {
    if (op.kind === 'delete') await this.dropUpdates(scope, op.txId); // delete beats edit
    const id = op.kind === 'create' ? (op.body.client_id ?? crypto.randomUUID()) : crypto.randomUUID();
    const entry: OutboxEntry = { ...op, ...scope, id, createdAt: nextStamp(), status: 'pending', attempts: 0 };
    await this.store.put(entry);
    this.changed();
    return entry;
  }

  private async dropUpdates(scope: Scope, txId: string) {
    for (const e of await this.list(scope)) {
      if (e.kind === 'update' && e.txId === txId) await this.store.remove(e.id);
    }
  }

  async discard(id: string): Promise<void> {
    await this.store.remove(id);
    this.changed();
  }

  /** Puts a failed entry back in line (keeps its original position). */
  async retry(id: string, scope: Scope): Promise<void> {
    const entry = (await this.list(scope)).find((e) => e.id === id);
    if (entry) await this.store.put({ ...entry, status: 'pending' });
    this.changed();
  }

  /** Sign-out on this device: nothing of this user may stay behind. */
  async clear(): Promise<void> {
    await this.store.clear();
    this.changed();
  }

  /** Entries of another account on this device can never be delivered; drop them. */
  async dropOthers(userId: string): Promise<void> {
    for (const e of await this.store.all()) if (e.userId !== userId) await this.store.remove(e.id);
    this.changed();
  }

  async flush(send: Send, scope: Scope): Promise<FlushResult> {
    const result: FlushResult = { sent: 0, failed: 0, stopped: false };
    for (const e of (await this.list(scope)).filter((x) => x.status === 'pending')) {
      try {
        await send(e);
        result.sent++;
        await this.store.remove(e.id);
      } catch (err) {
        await this.settle(e, judge(e, err), result);
      }
      if (result.stopped) break;
    }
    this.changed();
    return result;
  }

  private async settle(e: OutboxEntry, verdict: Verdict, result: FlushResult) {
    if (verdict === 'done') return this.store.remove(e.id);
    const attempts = e.attempts + 1;
    if (verdict === 'failed') {
      result.failed++;
      return this.store.put({ ...e, status: 'failed', attempts });
    }
    result.stopped = true;
    return this.store.put({ ...e, attempts });
  }
}
